import type { NavMonthlyPoint } from '@nav/shared';

import { formatPctPlain } from '@/lib/format';
import type { InsightItem } from './marketContext';
import type { SflProduct } from './sflProducts';

/**
 * Rule-based insight generation — the "Data Live" cards.
 *
 * Zero API cost, zero hallucination risk, and it can never drift out of sync
 * with the live NAV chart above it, because it reads the exact same
 * `NavMonthlyPoint[]` the chart renders. That property is why these cards were
 * not replaced when AI-written cards arrived: they are the floor the page always
 * has, including when the AI feature is switched off entirely.
 *
 * This deliberately does NOT explain macro causality ("why did the Fed do X") —
 * that requires real-world news judgement a template cannot supply. It only
 * narrates what the numbers themselves show: direction, magnitude, and how the
 * latest point compares to recent history.
 *
 * Macro causality is now supplied by the "Riset AI" cards
 * (`/api/funds/:code/insights`), which are researched with citations, checked
 * against the fund's own figures by a server-side guard, and published only
 * after an analyst approves them. The three kinds carry three different badges
 * so a reader is never left guessing which is which — see `SflInsightGrid`.
 */

function trailingAverage(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** Last `n` non-null values of a field, excluding the most recent point. */
function trailingValues(
  points: NavMonthlyPoint[],
  field: 'momPct' | 'yoyPct',
  n: number,
): number[] {
  const history = points.slice(0, -1);
  const values: number[] = [];
  for (let i = history.length - 1; i >= 0 && values.length < n; i--) {
    const v = history[i]![field];
    if (v != null) values.push(v);
  }
  return values;
}

function magnitudeWord(deltaAbs: number): string {
  if (deltaAbs < 0.05) return 'relatif stabil';
  if (deltaAbs < 0.3) return 'sedikit bergeser';
  if (deltaAbs < 1) return 'bergeser cukup terasa';
  return 'bergeser signifikan';
}

/**
 * "Tren YoY (Data Live)" — compares the latest YoY% to the previous month's
 * YoY%, computed straight from the series, no hard-coded numbers.
 */
function yoyInsight(points: NavMonthlyPoint[], product: SflProduct): InsightItem {
  const latest = points[points.length - 1];
  const prev = points.length >= 2 ? points[points.length - 2] : null;

  if (!latest || latest.yoyPct == null) {
    return {
      kicker: 'Tren YoY',
      title: 'Belum cukup data untuk menghitung YoY',
      body: `YoY membutuhkan NAV dari 12 bulan sebelumnya. Unggah lebih banyak riwayat NAV bulanan ${product.shortName} untuk mengaktifkan insight ini.`,
      tone: 'neutral',
      source: 'auto',
    };
  }

  if (prev == null || prev.yoyPct == null) {
    return {
      kicker: 'Tren YoY',
      title: `YoY tercatat ${formatPctPlain(latest.yoyPct)} pada ${latest.label}`,
      body: `Titik data sebelumnya belum memiliki YoY untuk dibandingkan, jadi arah trennya belum bisa dihitung otomatis. Angka ini dihitung langsung dari NAV live ${product.shortName}, bukan editorial.`,
      tone: 'accent',
      source: 'auto',
    };
  }

  const delta = latest.yoyPct - prev.yoyPct;
  const deltaAbs = Math.abs(delta);
  const arrow = delta >= 0 ? '▲' : '▼';
  const arah = delta > 0 ? 'naik' : delta < 0 ? 'turun' : 'stabil';
  const tone: InsightItem['tone'] = delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral';

  return {
    kicker: 'Tren YoY · Data Live',
    title: `YoY ${arah} dari ${formatPctPlain(prev.yoyPct)} menjadi ${formatPctPlain(latest.yoyPct)} di ${latest.label}`,
    body: `Year-on-year ${product.shortName} ${arah} ${formatPctPlain(deltaAbs)} poin dibanding bulan sebelumnya (${arrow} ${formatPctPlain(prev.yoyPct)} → ${formatPctPlain(latest.yoyPct)}), tergolong ${magnitudeWord(deltaAbs)}. Ini mencerminkan laju pertumbuhan tahunan berjalan, dihitung otomatis dari NAV bulanan terbaru — bukan indikasi untung/rugi absolut, karena itu tergantung arah NAV itu sendiri, bukan hanya YoY-nya.`,
    tone,
    source: 'auto',
  };
}

/**
 * "Momentum Bulanan (Data Live)" — compares the latest MoM% both to the
 * previous month and to the trailing 6-month average, so a single noisy
 * month doesn't read as a full trend reversal.
 */
function momentumInsight(points: NavMonthlyPoint[], product: SflProduct): InsightItem {
  const latest = points[points.length - 1];

  if (!latest || latest.momPct == null) {
    return {
      kicker: 'Momentum Bulanan',
      title: 'Belum cukup data untuk menghitung growth bulanan',
      body: `Growth bulanan membutuhkan minimal dua bulan NAV berurutan. Unggah bulan berikutnya untuk mengaktifkan insight ini.`,
      tone: 'neutral',
      source: 'auto',
    };
  }

  const recent = trailingValues(points, 'momPct', 6);
  const avg = trailingAverage(recent);
  const prevMom = recent[0] ?? null;

  if (avg == null || prevMom == null) {
    return {
      kicker: 'Momentum Bulanan · Data Live',
      title: `Growth bulanan tercatat ${formatPctPlain(latest.momPct)} pada ${latest.label}`,
      body: `Belum ada cukup riwayat bulan sebelumnya untuk membandingkan momentum. Angka ini dihitung langsung dari NAV live ${product.shortName}.`,
      tone: 'accent',
      source: 'auto',
    };
  }

  const vsPrevDelta = latest.momPct - prevMom;
  const vsAvgDelta = latest.momPct - avg;
  const vsPrevArrow = vsPrevDelta >= 0 ? '▲' : '▼';
  const vsAvgWord = vsAvgDelta > 0 ? 'di atas' : vsAvgDelta < 0 ? 'di bawah' : 'sejajar dengan';
  const tone: InsightItem['tone'] =
    vsPrevDelta > 0 && vsAvgDelta > 0 ? 'positive' : vsPrevDelta < 0 && vsAvgDelta < 0 ? 'negative' : 'neutral';

  return {
    kicker: 'Momentum Bulanan · Data Live',
    title: `Growth bulanan ${formatPctPlain(latest.momPct)} pada ${latest.label}, ${vsAvgWord} rata-rata ${recent.length} bulan terakhir`,
    body: `Pertumbuhan NAV ${product.shortName} bulan ini tercatat ${formatPctPlain(latest.momPct)} (${vsPrevArrow} dibanding ${formatPctPlain(prevMom)} bulan sebelumnya), dan ${vsAvgWord} rata-rata ${formatPctPlain(avg)} dari ${recent.length} bulan terakhir. Dihitung otomatis dari NAV bulanan live setiap kali data baru diunggah.`,
    tone,
    source: 'auto',
  };
}

/**
 * Two data-driven insight cards computed from the fund's own monthly series.
 * Pairs with the two hand-written macro-context cards in `marketContext.ts`
 * to fill the 4-card "Insight & Analisa" grid.
 */
export function computeDataInsights(points: NavMonthlyPoint[], product: SflProduct): InsightItem[] {
  return [yoyInsight(points, product), momentumInsight(points, product)];
}
